import React, { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { NeedHelpButton } from "@/components/need-help-button";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import { useShadcnInsetDynamicPageTitle } from "@/hooks/useShadcnInsetBreadcrumbTrail";
import {
  WORKSPACE_HUB_BREADCRUMB,
  WORKSPACE_HUB_PATH,
  isWorkspaceHubNavState,
} from "../Workspace/workspaceRoutes";
import { ExecutionRulesSimulatorPanel } from "./index";
import { SIMULATOR_LIST_PATH } from "./simulatorRoutes";

/**
 * Standalone simulation results (opened from the new-simulation wizard after Generate).
 */
export default function SimulatorResultsPage(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const shadcnInset = useShadcnInsetChrome();
  const { setActions: setInsetHeaderActions } = useShadcnInsetHeaderActions();
  const { selectedRunId, backtestId } = React.useMemo(() => {
    const q = new URLSearchParams(location.search);
    const run = q.get("run")?.trim() || null;
    const backtest = q.get("backtest")?.trim() || null;
    return { selectedRunId: run, backtestId: backtest };
  }, [location.search]);

  const fromWorkspace = isWorkspaceHubNavState(location.state);
  const exitPath = fromWorkspace ? WORKSPACE_HUB_PATH : SIMULATOR_LIST_PATH;
  const exitLabel = fromWorkspace ? "Back to workspace" : "Back to simulator";

  const [simulationTitle, setSimulationTitle] = React.useState<string | null>(null);
  useShadcnInsetDynamicPageTitle(simulationTitle, {
    parent: fromWorkspace ? WORKSPACE_HUB_BREADCRUMB : null,
  });

  const simulatorHelpHeaderActions = useMemo(
    () => <NeedHelpButton href="https://help.tesseratrades.com/simulator" />,
    []
  );

  useEffect(() => {
    if (!shadcnInset) {
      setInsetHeaderActions(null);
      return;
    }
    setInsetHeaderActions(simulatorHelpHeaderActions);
    return () => setInsetHeaderActions(null);
  }, [setInsetHeaderActions, shadcnInset, simulatorHelpHeaderActions]);

  return (
    <ExecutionRulesSimulatorPanel
      variant="standalone"
      resultsOnlyLayout
      initialBacktestId={backtestId ?? undefined}
      initialSelectedRunId={selectedRunId ?? (backtestId ? `saved_${backtestId}` : null)}
      wizardExitHref={exitPath}
      wizardExitLabel={exitLabel}
      savedSimulationsHref={exitPath}
      onResultsExit={() => history.push(exitPath)}
      onActiveSimulationTitleChange={setSimulationTitle}
    />
  );
}
